import { useEffect, useLayoutEffect, useRef, useState } from "react"
import { css } from "@emotion/react"
import styled from "@emotion/styled"
import { motion, useTransform, useInView, useScroll } from "framer-motion"
import useReducedMotion from "hooks/useReducedMotion"

import { useConsole } from "contexts/Console"
import { useViewport } from "contexts/Viewport"
import { fullGrid } from "css/grid"

import Canvas from "./Canvas"
import { ErsiveProvider, useErsive } from "./context"
import Sequence from "./Sequence"
import TextBlock from "./TextBlock"
import ScrollerPassive from "./reduced-motion/ScrollerPassive"
import FigCaption, { isInnerCN, fullWidthInnerStyle } from "components/figcaption/FigCaption"

const pseudoStyles = css`
  content: "";
  display: block;
  position: absolute;
  left: 0;
  width: 100%;
  height: 20vh;
  z-index: 0;
`

const Root = styled(motion.section)`
  ${fullGrid}
  position: relative;
  width: 100vw;
  height: 100vh;
  transform: translate3d(0, 0, 0);
`

const Gradients = styled(motion.div)`
  position: sticky;
  top: 0;
  grid-column: doc;
  grid-row: 1;
  height: 100vh;

  transform: translate3d(0, 0, 0);

  &.top-grad::before {
    ${pseudoStyles}
    top: 0;
    background: rgb(var(--top-color));
    mask-image: linear-gradient(black, transparent);
    -webkit-mask-image: linear-gradient(black, transparent);
  }

  &.bot-grad::after {
    ${pseudoStyles}
    bottom: 0;
    background: rgb(var(--bot-color));
    mask-image: linear-gradient(transparent, black);
    -webkit-mask-image: linear-gradient(transparent, black);
  }
`

const CanContainer = styled(motion.div)`
  position: sticky;
  top: 0;
  height: 100vh;
  grid-column: doc;
  grid-row: 1;
  // display: grid;
`

const FCaption = styled.div`
  position: sticky;
  top: 0;
  height: 100vh;
  grid-column: doc;
  grid-row: 1;
  display: grid;
  z-index: 5;
  ${fullGrid};

  & figcaption.${isInnerCN} {
    ${fullWidthInnerStyle};
  }
`

function TopGrad({ color, prog }) {
  const opacity = useTransform(prog, [0, 0.15], [1, 0])
  return <Gradients className='top-grad' style={{ "--top-color": color, opacity }} />
}
function BotGrad({ color, prog }) {
  const opacity = useTransform(prog, [0.85, 1], [0, 1])
  return <Gradients className='bot-grad' style={{ "--bot-color": color, opacity }} />
}

function Ersive({ image_sequence, text_stop, transition_gradient, fig_caption, ...props }) {
  const console = useConsole()

  const viewport = useViewport()

  const ref = useRef()
  const ofsTopRef = useRef()

  const { rootHeight } = useErsive()
  const isInView = useInView(ref, { once: true, margin: "100% 0% 120% 0%" })

  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] })

  useLayoutEffect(() => {
    const r = ref.current

    const resizeObserver = new ResizeObserver(entries => {
      for (const entry of entries) {
        if (entry.contentBoxSize) {
          ofsTopRef.current = ref.current.offsetTop
        }
      }
    })

    resizeObserver.observe(r)

    return () => resizeObserver.unobserve(r)
  }, [])

  return (
    <>
      {isInView ? <Sequence {...image_sequence} prog={scrollYProgress} /> : null}
      <Root ref={ref} style={{ height: rootHeight }}>
        <CanContainer>
          <Canvas />
        </CanContainer>
        {fig_caption?.caption?.length ? (
          <FCaption>
            <FigCaption {...fig_caption} />
          </FCaption>
        ) : null}
        {transition_gradient?.top_color ? <TopGrad className='top-grad' prog={scrollYProgress} color={transition_gradient.top_color} /> : null}
        {transition_gradient?.bottom_color ? <BotGrad className='bot-grad' prog={scrollYProgress} color={transition_gradient.bottom_color} /> : null}
        {text_stop.length ? <TextBlock text_stop={text_stop} prog={scrollYProgress} /> : null}
      </Root>
    </>
  )
}

function Lersive({ ...props }) {
  return (
    <ErsiveProvider
      first_frame={props.image_sequence.first_frame_cld}
      start={props.image_sequence.start}
      end={props.image_sequence.end}
      text_stop={props.text_stop}
    >
      <Ersive {...props} />
    </ErsiveProvider>
  )
}

export default function Scrollersive(props) {
  const prefersReducedMotion = useReducedMotion()

  return prefersReducedMotion ? <ScrollerPassive {...props} /> : <Lersive {...props} />
}
